import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { currencyString, constants } from "@qgiv/core-js";
import { getFormattedPrice } from "@qgiv/core-donor";
import { selectReceipt } from "../../../redux/slices/receiptSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import {
    selectCmsControlByIdByPage,
    selectThemeSettings,
} from "../../../redux/slices/cmsSettingsSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import { ThemeType } from "../../../redux/constants";
import ReceiptParticipant from "./ReceiptParticipant";

const ConnectedReceiptParticipant = ({
    controlId,
    id /* references registration id */,
}) => {
    const {
        ENUMS: { DefaultView, ActivityRole, CategorySetting },
    } = constants;
    const { MULTI_CATEGORY } = CategorySetting;
    const isCms = useSelector(selectIsCms);
    const receipt = useSelector(selectReceipt);
    const formSettings = useSelector(selectAllFormSettings);
    const {
        transaction: { registrations = [] },
    } = receipt;
    const { currency } = formSettings;
    const registration = registrations.find(
        (_registration) => _registration.id === id,
    );
    const {
        participantTotal = 0.0,
        originalCategoryFee,
        managedByPrimary = false,
        commitmentAmount = 0,
    } = registration;
    const p2pSettings = useSelector(selectAllP2pSettings);
    const {
        categorySystemType,
        registrationFeeDisplay,
        registrationFeeDisplayText,
    } = p2pSettings;
    const isMultiCategory = categorySystemType === MULTI_CATEGORY;
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_REGISTRATION_RECEIPT,
            state,
        ),
    );

    const {
        options: { registrationLabel = "Registrant %RegistrationID%" },
    } = control;

    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.SUMMARY, state),
    );

    const {
        p2pRegistration: {
            managedAdditionalParticipantLabel = "You're managing their fundraising",
            nonFundraisingParticipantLabel = "Not Fundraising",
            teamCaptainLabel = "Captain",
            commitmentSummaryLabel = "%CommitmentAmount% commitment",
        },
    } = themeSettings;

    // TODO: replace with data from receipt
    const isManagedByPrimary = !!managedByPrimary;

    const hasPromo = !isCms && originalCategoryFee !== participantTotal;

    const formattedPrePromoPrice = currencyString(
        originalCategoryFee,
        currency,
    );

    const hasCommitment = registration && registration.hasCommitment === 1;

    const receiptParticipantProps = {
        commitmentSummaryLabel: commitmentSummaryLabel.replace(
            "%CommitmentAmount%",
            currencyString(commitmentAmount, currency),
        ),
        ...registration,
        nfp: registration.activityRole === ActivityRole.NONFUNDRAISING,
        nonFundraisingParticipantLabel,
        isManagedByPrimary,
        managedAdditionalParticipantLabel,
        registrationLabel: registrationLabel.replace(
            "%RegistrationID%",
            `#${id}`,
        ),
        participantTotal: getFormattedPrice(
            participantTotal,
            currency,
            registrationFeeDisplay,
            registrationFeeDisplayText,
            hasPromo,
        ),
        teamCaptainLabel,
        hasPromo,
        formattedPrePromoPrice,
        isMultiCategory,
        hasCommitment,
    };

    return <ReceiptParticipant {...receiptParticipantProps} />;
};

ConnectedReceiptParticipant.propTypes = {
    controlId: PropTypes.number.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ConnectedReceiptParticipant;
